/**
 * Which tiles personalize for a split field and an audience, and which are left out (no query
 * runs). Nothing is fetched until both are known.
 */
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { getApiErrorMessage } from "../services/apiErrors";
import { checkSplit } from "../services/distributions/distributionsApi";
import type { AudienceApi, SplitCheckApi, SplitFieldApi } from "../services/distributions/distributions.types";

/** Stable cache identity of a split field (model release + field ref + role). */
export const splitFieldKey = (split: SplitFieldApi): string =>
  [split.model_release_id, split.field.kind, split.field.model_field_id, split.field.role ?? ""].join(":");

export interface UseSplitCheckResult {
  splitCheck: SplitCheckApi | null;
  isLoading: boolean;
  errorMessage: string | null;
  refetch: () => void;
}

export function useSplitCheck(
  storyId: string | undefined,
  split: SplitFieldApi | null,
  audience: AudienceApi | null,
): UseSplitCheckResult {
  const query = useQuery({
    queryKey: queryKeys.distributions.splitCheck(storyId ?? "", split ? splitFieldKey(split) : "", audience ?? ""),
    queryFn: () => checkSplit(storyId as string, { split: split as SplitFieldApi, audience: audience as AudienceApi }),
    enabled: Boolean(storyId && split && audience),
    staleTime: 60_000,
  });
  return {
    splitCheck: query.data ?? null,
    isLoading: query.isLoading,
    errorMessage: query.isError ? getApiErrorMessage(query.error, "Couldn’t check which tiles personalize.") : null,
    refetch: () => void query.refetch(),
  };
}
